import React from 'react';
import styled from 'styled-components/macro';
import { connect } from 'microfronts-redux';
import {
  Avatar,
  Card,
  Chip,
  Divider,
  Icon,
  Paragraph,
  Text,
  Timeline,
  TimelineItem,
  tokens
} from '@freightos/design-system';

import { DEAL_IMAGES } from 'slimSearch/components/hotDeals/constants';
import { ANALYTICS_EVENT_NAME, MODES } from 'slimSearch/constants';

import { t } from 'utils/translationProvider';
import { moneyFormat } from 'utils';

import { navigateToResults, searchAnalytics } from 'slimSearch/actions';
import { cleanResults } from 'results/results.actions';
import { isMobileSelector } from 'slimSearch/selectors';

const getLabel = (locationType) => {
  if (locationType.type === 'airport' || locationType.type === 'seaport') {
    return locationType.value + ', ' + locationType.city;
  } else {
    return locationType.value;
  }
};

const ValueBadge = ({ deal }) => {
  if (deal.onlyInsurance) {
    return (
      <StyledChip type="warning">
        {t('hotDeals/insuranceIncluded', 'Insurance included')}
      </StyledChip>
    );
  }

  if (deal.services) {
    return <StyledChip type="positive">{t('hotDeals/bestValue', 'Best value')}</StyledChip>;
  }

  return <StyledChip type="default">{t('hotDeals/cheapest', 'Cheapest')}</StyledChip>;
};

const getDealImage = (item) => {
  const random = Math.floor(Math.random() * 4);

  if (DEAL_IMAGES[item]) {
    return DEAL_IMAGES[item][random];
  }

  return null;
};

export const Deal = ({
  item,
  index,
  cleanResults,
  navigateToResults,
  searchAnalytics,
  isMobile
}) => {
  return (
    <StyledCard
      data-test-id={`hot-deal-${index}`}
      isMobile={isMobile}
      size="small"
      title={null}
      onClick={() => {
        searchAnalytics({
          name: ANALYTICS_EVENT_NAME.HOT_OFFER_BUTTON_CLICKED,
          data: {
            rfq: item.rfq
          }
        });

        cleanResults();
        navigateToResults({ rfq: item.rfq });
      }}
      shadow={false}
      bordered
      cover={
        <Cover image={getDealImage(item.mode)} data-test-id="hot-deal-image">
          <CoverRow>
            <ValueBadge deal={item} data-test-id="hot-deal-badge" />
            <Mode data-test-id="hot-deal-mode">
              {MODES[item.mode]?.icon && <Icon size="tiny" type={MODES[item.mode].icon} />}{' '}
              {item.mode} {item.load.value}
            </Mode>

            <Price data-test-id="hot-deal-price-container">
              <Text>{t('hotDeals/priceStartFrom', 'Starting from')}</Text>
              <div data-test-id="hot-deal-total-price">{moneyFormat(item.totalPrice)}</div>
            </Price>
          </CoverRow>
        </Cover>
      }
    >
      <StyledTimeLine>
        <TimelineItem
          dot={
            <Avatar
              bordered
              size="small"
              country={item.origin.country}
              type="flag"
              data-test-id={`hot-deal-origin-${item.origin.country}`}
            />
          }
        >
          <Route strong ellipsis data-test-id={`hot-deal-origin-label`}>
            {getLabel(item.origin)}
          </Route>
        </TimelineItem>
        <TimelineItem
          dot={
            <Avatar
              bordered
              size="small"
              country={item.destination.country}
              type="flag"
              data-test-id={`hot-deal-destination-${item.destination.country}`}
            />
          }
        >
          <Route strong ellipsis data-test-id={`hot-deal-destination-label`}>
            {getLabel(item.destination)}
          </Route>
        </TimelineItem>
      </StyledTimeLine>
      <StyledDivider />
      <StyledProviderParagraph ellipsis type="secondary" data-test-id="hot-deal-provider">
        {item.rating ? <Icon type="star-full" /> : null} {item.rating} {item.rating ? '|' : ''}{' '}
        {item.provider}
      </StyledProviderParagraph>
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  &&& {
    box-shadow: none;
    margin: 0;
    padding: 0;
    width: 250px;
    flex-grow: 1;

    .ant-card-body {
      margin: 20px;
    }

    &:hover {
      box-shadow: ${tokens.fdsBoxShadowMd};
      cursor: pointer;
    }
  }
`;

const Route = styled(Paragraph)`
  margin-left: ${tokens.fdsComponentSpacingSm};
  margin-bottom: 0;
`;

const StyledTimeLine = styled(Timeline)`
  .ant-timeline-item-last {
    margin: 0 0 -30px 0;
    padding: 0;
  }

  .ant-timeline-item-tail {
    border-left: 2px dashed ${tokens.fdsComponentSecondaryColorDisabled};
  }
`;

const Cover = styled.div`
  background: url(${({ image }) => (image ? image : '')}) no-repeat;
  background-size: cover;
  height: 100px;
  width: 100%;
  color: ${tokens.fdsColorPrimaryWhite};
  margin: 0;
  border-radius: 4px 4px 0 0;

  &:before {
    content: '';
    display: block;
    position: absolute;
    height: 100px;
    top: 0;
    left: 0;
    right: 0;
    background: ${tokens.fdsColorPrimaryBlue50}99; // 99 = 60% opacity
    border-radius: 4px 4px 0 0;
  }
`;

const CoverRow = styled.div`
  color: ${tokens.fdsColorPrimaryWhite};
  position: relative;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: end;
  height: 100%;
  padding: 8px 16px;

  &:after {
    z-index: 1;
  }
`;

const Mode = styled.div`
  font-size: ${tokens.fdsFontSizeSm};
  font-weight: ${tokens.fdsFontWeightSemibold};
`;

const StyledDivider = styled(Divider)`
  margin: ${tokens.fdsCheckboxSize} 0;
`;

const Price = styled.div`
  text-align: right;
  font-size: ${tokens.fdsH5FontSize};
  font-weight: ${tokens.fdsFontWeightBold};

  & .ant-typography {
    color: ${tokens.fdsColorPrimaryWhite};
    font-size: 14px;
    font-weight: 400;
  }
`;

const StyledProviderParagraph = styled(Paragraph)`
  margin-bottom: 0;
`;

const StyledChip = styled(Chip)`
  position: absolute;
  top: 16px;
`;
const mapStateToProps = (store) => ({
  isMobile: isMobileSelector(store)
});

export default connect(mapStateToProps, {
  searchAnalytics,
  cleanResults,
  navigateToResults
})(Deal);
